import { useRef, useState } from 'react'
import { Upload, RefreshCw } from 'lucide-react'
import axios from '../../../../lib/axios'
import CommonSettings from './CommonSettings'

export default function ImageSettings({ block, onUpdate }) {
  const { settings } = block
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await axios.post('master-templates/upload-file/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      if (res.data?.url) {
        onUpdate({ src: res.data.url, alt: settings.alt || file.name })
      }
    } catch (err) {
      console.error('Image upload failed:', err)
      // Fallback: use local object URL for preview
      const localUrl = URL.createObjectURL(file)
      onUpdate({ src: localUrl, alt: settings.alt || file.name })
    } finally {
      setUploading(false)
      // Reset so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // Parse width percentage value for the slider
  const getWidthPercent = () => {
    const w = settings.width || '100%'
    if (w.includes('%')) return parseInt(w) || 100
    return 100
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>Image Settings</h4>

      {/* Image Upload / Preview */}
      <div>
        <label style={{ fontSize: '0.8rem', fontWeight: '500', display: 'block', marginBottom: '0.4rem', color: '#475569' }}>
          Image
        </label>

        {settings.src ? (
          <div style={{
            position: 'relative',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
            background: '#f8fafc'
          }}>
            <img
              src={settings.src}
              alt={settings.alt || ''}
              style={{
                display: 'block',
                width: '100%',
                maxHeight: '140px',
                objectFit: 'contain',
                background: '#f1f5f9'
              }}
              onError={(e) => {
                e.target.style.display = 'none'
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.35rem',
                width: '100%',
                padding: '0.5rem',
                border: 'none',
                borderTop: '1px solid #e5e7eb',
                background: uploading ? '#f1f5f9' : '#fff',
                color: uploading ? '#94a3b8' : 'var(--color-primary)',
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: uploading ? 'wait' : 'pointer',
                transition: 'background 0.15s'
              }}
              onMouseOver={(e) => { if (!uploading) e.currentTarget.style.background = '#f8fafc' }}
              onMouseOut={(e) => { if (!uploading) e.currentTarget.style.background = '#fff' }}
            >
              {uploading ? (
                <><RefreshCw size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Uploading...</>
              ) : (
                <><RefreshCw size={13} /> Change Image</>
              )}
            </button>
          </div>
        ) : (
          <label style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '1.25rem',
            border: '2px dashed #cbd5e1',
            borderRadius: '8px',
            cursor: uploading ? 'wait' : 'pointer',
            background: '#f8fafc',
            transition: 'all 0.2s'
          }}>
            {uploading ? (
              <RefreshCw size={20} style={{ color: '#94a3b8', animation: 'spin 0.8s linear infinite' }} />
            ) : (
              <Upload size={20} style={{ color: '#94a3b8' }} />
            )}
            <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500' }}>
              {uploading ? 'Uploading...' : 'Click to upload image'}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        )}

        {/* Hidden file input for "Change Image" button */}
        {settings.src && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        )}
      </div>

      {/* Image URL */}
      <div>
        <label style={{ fontSize: '0.8rem', fontWeight: '500', display: 'block', marginBottom: '0.4rem', color: '#475569' }}>
          Image URL
        </label>
        <input
          type="text"
          value={settings.src || ''}
          onChange={(e) => onUpdate({ src: e.target.value })}
          placeholder="https://example.com/image.jpg"
          style={{ width: '100%', fontSize: '0.8rem', padding: '0.5rem' }}
        />
      </div>

      {/* Alt Text */}
      <div>
        <label style={{ fontSize: '0.8rem', fontWeight: '500', display: 'block', marginBottom: '0.4rem', color: '#475569' }}>
          Alt Text
        </label>
        <input
          type="text"
          value={settings.alt || ''}
          onChange={(e) => onUpdate({ alt: e.target.value })}
          placeholder="Describe this image"
          style={{ width: '100%', fontSize: '0.8rem', padding: '0.5rem' }}
        />
      </div>

      {/* Width */}
      <div>
        <label style={{ fontSize: '0.8rem', fontWeight: '500', display: 'block', marginBottom: '0.4rem', color: '#475569' }}>
          Width: {settings.width || '100%'}
        </label>
        <input
          type="range"
          min="10"
          max="100"
          step="5"
          value={getWidthPercent()}
          onChange={(e) => onUpdate({ width: `${e.target.value}%` })}
          style={{ width: '100%', cursor: 'pointer' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#94a3b8', marginTop: '0.2rem' }}>
          <span>10%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Height */}
      <div>
        <label style={{ fontSize: '0.8rem', fontWeight: '500', display: 'block', marginBottom: '0.4rem', color: '#475569' }}>
          Height
        </label>
        <select
          value={settings.height === 'auto' ? 'auto' : 'custom'}
          onChange={(e) => onUpdate({ height: e.target.value === 'auto' ? 'auto' : '200px' })}
          style={{ width: '100%', fontSize: '0.8rem', padding: '0.4rem', marginBottom: '0.4rem' }}
        >
          <option value="auto">Auto (proportional)</option>
          <option value="custom">Custom</option>
        </select>
        {settings.height !== 'auto' && (
          <input
            type="text"
            value={settings.height || ''}
            onChange={(e) => onUpdate({ height: e.target.value })}
            placeholder="e.g., 200px"
            style={{ width: '100%', fontSize: '0.8rem', padding: '0.5rem' }}
          />
        )}
      </div>

      {/* Link URL */}
      <div>
        <label style={{ fontSize: '0.8rem', fontWeight: '500', display: 'block', marginBottom: '0.4rem', color: '#475569' }}>
          Link URL (optional)
        </label>
        <input
          type="text"
          value={settings.link || ''}
          onChange={(e) => onUpdate({ link: e.target.value })}
          placeholder="https://example.com"
          style={{ width: '100%', fontSize: '0.8rem', padding: '0.5rem' }}
        />
      </div>

      {/* Alignment */}
      <div>
        <label style={{ fontSize: '0.8rem', fontWeight: '500', display: 'block', marginBottom: '0.4rem', color: '#475569' }}>
          Alignment
        </label>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          {['left', 'center', 'right'].map(align => (
            <button
              key={align}
              onClick={() => onUpdate({ textAlign: align })}
              style={{
                flex: 1,
                padding: '0.4rem',
                border: settings.textAlign === align ? '2px solid var(--color-primary)' : '1px solid #e5e7eb',
                borderRadius: '6px',
                background: settings.textAlign === align ? 'rgba(99, 102, 241, 0.08)' : '#fff',
                color: settings.textAlign === align ? 'var(--color-primary)' : '#64748b',
                fontWeight: settings.textAlign === align ? '600' : '400',
                fontSize: '0.75rem',
                cursor: 'pointer',
                textTransform: 'capitalize'
              }}
            >
              {align}
            </button>
          ))}
        </div>
      </div>

      <CommonSettings settings={settings} onUpdate={onUpdate} />

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
